import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    FormFieldInputNew,
    FormFieldSelect,
    FormFieldUploadFile,
    Layout,
} from '@netapp/bxp-design-system-react';
import { PROXY_TYPES } from 'modules/add-agent/components/common/ProxyConfiguration';
import { extractApiFailureMessage } from 'utils/api';
import { apiClient } from 'utils/clients';
import { configurations as i18nNamespace } from '../../../../i18n/namespace';
import styles from '../../style.module.scss';

export const PROXY_ACCESS_FIELDS = {
    PROXY_TYPE: 'proxyType',
    PROXY_PROTOCOL: 'proxyProtocol',
    PROXY_HOST: 'proxyHost',
    PROXY_PORT: 'proxyPort',
    PROXY_USER_NAME: 'proxyUserName',
    PROXY_PASSWORD: 'proxyPassword',
    CERTIFICATE_FILE: 'certificateFile',
};

export const getProxyTypeOptions = t => [
    { label: t('no_proxy'), value: PROXY_TYPES.NONE },
    { label: t('explicit_proxy'), value: PROXY_TYPES.EXPLICIT },
    { label: t('transparent_proxy'), value: PROXY_TYPES.TRANSPARENT },
];

export const parseProxyUri = uri => {
    if (!uri) {
        return { protocol: 'http', host: '', port: '' };
    }
    try {
        const parsed = new URL(uri.includes('://') ? uri : `http://${uri}`);
        return {
            protocol: parsed.protocol.replace(':', ''),
            host: parsed.hostname,
            port: parsed.port || (parsed.protocol === 'https:' ? '443' : '80'),
        };
    } catch (_e) {
        return { protocol: 'http', host: uri, port: '' };
    }
};

export const buildProxyUri = (host, port, protocol = 'http') => {
    if (!host) {
        return '';
    }
    const hostname = host.replace(/^(https?):\/\//i, '');
    return port ? `${protocol}://${hostname}:${port}` : `${protocol}://${hostname}`;
};

export const isProxyConfigurationComplete = formState => {
    const proxyType = formState[PROXY_ACCESS_FIELDS.PROXY_TYPE]?.value;
    if (!proxyType) {
        return false;
    }
    if (proxyType === PROXY_TYPES.NONE) {
        return true;
    }
    if (proxyType === PROXY_TYPES.TRANSPARENT) {
        return Boolean(formState[PROXY_ACCESS_FIELDS.CERTIFICATE_FILE]);
    }

    const port = formState[PROXY_ACCESS_FIELDS.PROXY_PORT];
    const portNumber = Number(port);
    const portIsValid = /^\d+$/.test(port) && portNumber >= 1 && portNumber <= 65535;
    const userName = formState[PROXY_ACCESS_FIELDS.PROXY_USER_NAME];
    const password = formState[PROXY_ACCESS_FIELDS.PROXY_PASSWORD];
    const credentialsComplete = (!userName && !password) || Boolean(userName && password);

    return Boolean(
        formState[PROXY_ACCESS_FIELDS.PROXY_HOST]
        && portIsValid
        && credentialsComplete
    );
};

export const getProxySubmitLabelKey = isConfigured => (isConfigured ? 'save' : 'configure');

export const isProxySubmitDisabled = (form, isConfigured) => {
    if (!isProxyConfigurationComplete(form?.formState)) {
        return true;
    }
    if (!isConfigured) {
        return false;
    }
    const hasChanges = Boolean(form?.isDirty) || Object.values(form?.dirty || {}).some(Boolean);
    return !hasChanges;
};

export const PROXY_API_HEADERS = {
    Accept: 'application/json',
};

export const getProxyApiUrl = () => `${process.env.REACT_APP_OCCM_ENDPOINT}system/config/proxy`;

// `extractApiFailureMessage` reads `detail` and the `invalidParams` reason the
// API reports for a rejected value, but falls back to the literal "Unknown",
// which is dropped here so the caller can use its own message instead.
export const getProxyApiErrorMessage = error => {
    const message = extractApiFailureMessage(error, 'detail');
    return message && message !== 'Unknown' ? message : '';
};

export const fetchProxyConfiguration = async ({ dispatch, t } = {}) => {
    try {
        const { data } = await apiClient.get(getProxyApiUrl(), { headers: PROXY_API_HEADERS });
        return data;
    } catch (error) {
        dispatch?.({
            type: 'NOTIFICATIONS:ERROR',
            payload: {
                message: t ? t('failed_to_fetch_proxy') : 'Failed to load the proxy configuration.',
                exception: error,
                moreInfo: getProxyApiErrorMessage(error) || undefined,
                key: 'GET-PROXY',
                showGlobal: true,
            },
        });
        return null;
    }
};

export const selectProxyType = proxyConfig => proxyConfig?.proxy_type?.toUpperCase();
export const selectProxyUrl = proxyConfig => {
    const proxyUrl = proxyConfig?.proxy_url;
    if (!proxyUrl) {
        return '';
    }
    return proxyUrl.replace(/^(https?):\/\//i, '');
};
export const selectHasCertificate = proxyConfig => Boolean(
    proxyConfig?.ca_cert || proxyConfig?.certificate
);

export const buildProxyApiPayload = formState => {
    const payload = {};
    const proxyType = formState?.[PROXY_ACCESS_FIELDS.PROXY_TYPE]?.value;
    if (proxyType) {
        payload.proxy_type = proxyType;
    }

    const proxyUrl = buildProxyUri(
        formState?.[PROXY_ACCESS_FIELDS.PROXY_HOST],
        formState?.[PROXY_ACCESS_FIELDS.PROXY_PORT],
        formState?.[PROXY_ACCESS_FIELDS.PROXY_PROTOCOL]
    );
    if (proxyUrl) {
        payload.proxy_url = proxyUrl;
    }

    const proxyUsername = formState?.[PROXY_ACCESS_FIELDS.PROXY_USER_NAME];
    if (proxyUsername) {
        payload.proxy_username = proxyUsername;
    }

    const proxyPassword = formState?.[PROXY_ACCESS_FIELDS.PROXY_PASSWORD];
    if (proxyPassword) {
        payload.proxy_password = proxyPassword;
    }

    const certificateFile = formState?.[PROXY_ACCESS_FIELDS.CERTIFICATE_FILE];
    const caCert = certificateFile?.contents || (typeof certificateFile === 'string' ? certificateFile : '');
    if (caCert) {
        payload.ca_cert = caCert;
    }

    return payload;
};

export const buildProxyApiPayloadFromConfig = proxyConfig => {
    if (!proxyConfig?.proxy_type) {
        return null;
    }
    const { protocol, host, port } = parseProxyUri(proxyConfig.proxy_url);
    return buildProxyApiPayload({
        [PROXY_ACCESS_FIELDS.PROXY_TYPE]: { value: proxyConfig.proxy_type?.toUpperCase() },
        [PROXY_ACCESS_FIELDS.PROXY_PROTOCOL]: protocol,
        [PROXY_ACCESS_FIELDS.PROXY_HOST]: host,
        [PROXY_ACCESS_FIELDS.PROXY_PORT]: port,
        [PROXY_ACCESS_FIELDS.PROXY_USER_NAME]: proxyConfig.proxy_username || '',
        [PROXY_ACCESS_FIELDS.PROXY_PASSWORD]: proxyConfig.proxy_password || '',
        [PROXY_ACCESS_FIELDS.CERTIFICATE_FILE]: proxyConfig.ca_cert || proxyConfig.certificate || '',
    });
};

export const requiresProxyTypeReset = (previousProxyType, nextProxyType) => {
    if (!previousProxyType || !nextProxyType || previousProxyType === nextProxyType) {
        return false;
    }
    const isSwitchableType = proxyType => (
        proxyType === PROXY_TYPES.EXPLICIT || proxyType === PROXY_TYPES.TRANSPARENT
    );
    return isSwitchableType(previousProxyType) && isSwitchableType(nextProxyType);
};

// An unconfigured proxy is created with PUT. Switching explicit <-> transparent
// is also a PUT so the new type replaces the current one in one request. Other
// updates to an existing proxy use PATCH.
export const submitProxyConfiguration = async ({
    formState,
    isConfigured,
    previousProxyType,
    dispatch,
    t,
}) => {
    const payload = buildProxyApiPayload(formState);
    const nextProxyType = formState?.[PROXY_ACCESS_FIELDS.PROXY_TYPE]?.value;
    const url = getProxyApiUrl();
    const requestConfig = { headers: PROXY_API_HEADERS };
    const isTypeSwitch = requiresProxyTypeReset(previousProxyType, nextProxyType);
    const notificationKey = isConfigured && !isTypeSwitch ? 'PATCH-PROXY' : 'PUT-PROXY';

    try {
        if (!isConfigured || isTypeSwitch) {
            await apiClient.put(url, payload, requestConfig);
        } else {
            await apiClient.patch(url, payload, requestConfig);
        }
        dispatch({
            type: 'NOTIFICATIONS:SUCCESS',
            payload: {
                message: t(isConfigured ? 'proxy_updated_successfully' : 'proxy_configured_successfully'),
                key: notificationKey,
                showGlobal: true,
            },
        });
        return { success: true };
    } catch (error) {
        return {
            success: false,
            errorMessage: getProxyApiErrorMessage(error)
                || t(isConfigured ? 'failed_to_update_proxy' : 'failed_to_configure_proxy'),
        };
    }
};

const ExplicitProxyFields = ({ form, t }) => (
    <>
        <Layout.GridItem lg={12}>
            <div className={styles.hostPortRow}>
                <div className={styles.hostField}>
                    <FormFieldInputNew
                        form={form}
                        name={PROXY_ACCESS_FIELDS.PROXY_HOST}
                        label={t('http_proxy')}
                    />
                </div>
                <span className={styles.hostPortSeparator}>:</span>
                <div className={styles.portField}>
                    <FormFieldInputNew
                        form={form}
                        name={PROXY_ACCESS_FIELDS.PROXY_PORT}
                        label={t('port')}
                    />
                </div>
            </div>
        </Layout.GridItem>
        <Layout.GridItem lg={12}>
            <FormFieldUploadFile
                form={form}
                name={PROXY_ACCESS_FIELDS.CERTIFICATE_FILE}
                label={t('upload_certificate')}
                placeholder={t('choose_a_certificate')}
                isOptional={true}
            />
        </Layout.GridItem>
        <Layout.GridItem lg={6} md={6} sm={12}>
            <FormFieldInputNew
                form={form}
                name={PROXY_ACCESS_FIELDS.PROXY_USER_NAME}
                label={t('username')}
                placeholder={t('username_placeholder')}
                isOptional={true}
                autoComplete="off"
            />
        </Layout.GridItem>
        <Layout.GridItem lg={6} md={6} sm={12}>
            <FormFieldInputNew
                form={form}
                name={PROXY_ACCESS_FIELDS.PROXY_PASSWORD}
                label={t('password')}
                type="password"
                isOptional={true}
            />
        </Layout.GridItem>
    </>
);

const TransparentProxyFields = ({ form, t }) => (
    <Layout.GridItem lg={12}>
        <FormFieldUploadFile
            form={form}
            name={PROXY_ACCESS_FIELDS.CERTIFICATE_FILE}
            label={t('upload_certificate')}
            placeholder={t('choose_a_certificate')}
        />
    </Layout.GridItem>
);

const ProxyConfiguration = ({
    form,
}) => {
    const { t } = useTranslation(i18nNamespace);
    const { formState } = form;
    const proxyType = formState[PROXY_ACCESS_FIELDS.PROXY_TYPE]?.value;
    const onProxyTypeChange = (value, event) => {
        form.handleFormChange(value, event);
        [
            PROXY_ACCESS_FIELDS.PROXY_HOST,
            PROXY_ACCESS_FIELDS.PROXY_PORT,
            PROXY_ACCESS_FIELDS.PROXY_USER_NAME,
            PROXY_ACCESS_FIELDS.PROXY_PASSWORD,
            PROXY_ACCESS_FIELDS.CERTIFICATE_FILE,
        ].forEach(name => {
            form.handleFormChange('', { name, ignoreDirty: true });
        });
    };

    return (
        <Layout.Grid rowGap="lg">
            <Layout.GridItem lg={6} md={6} sm={12}>
                <FormFieldSelect
                    form={form}
                    name={PROXY_ACCESS_FIELDS.PROXY_TYPE}
                    label={t('proxy_type')}
                    options={getProxyTypeOptions(t)}
                    onChange={onProxyTypeChange}
                />
            </Layout.GridItem>
            {proxyType === PROXY_TYPES.EXPLICIT && (
                <ExplicitProxyFields form={form} t={t} />
            )}
            {proxyType === PROXY_TYPES.TRANSPARENT && (
                <TransparentProxyFields form={form} t={t} />
            )}
        </Layout.Grid>
    );
};

export default React.memo(ProxyConfiguration);
